import type { TunaEditor } from '@tunasong/plugin-lib'
import type { CommandAnchor } from '@tunasong/schemas'
import { findBlockPath } from '@tunasong/agent-tools'

export { findBlockPath } from '@tunasong/agent-tools'

export type ResolveAnchorResult = { path: number[] } | { error: string }

/**
 * Resolves a stale-resilient `CommandAnchor` to an absolute Slate path.
 *
 * - `replace` / `before` → the block's own path (Slate inserts shift the
 *   block right, so `before` and `replace` share an insertion point; the
 *   caller distinguishes via the apply semantics).
 * - `after` → next-sibling slot at the same level.
 * - `inside-start` / `inside-end` → first or last child slot of the block,
 *   with `subPath` appended for nested targets.
 *
 * Returns `{ error }` when the anchor block is no longer in the tree —
 * the proposal flips to a stale state on accept rather than mutating
 * the wrong target.
 */
export const resolveAnchor = (editor: TunaEditor, anchor: CommandAnchor): ResolveAnchorResult => {
  const found = findBlockPath(editor as unknown as { children?: unknown[] }, anchor.id)
  if (!found) {
    return { error: `anchor block not found: ${anchor.id}` }
  }
  const { node, path } = found
  const childCount = Array.isArray(node.children) ? node.children.length : 0
  const sub = anchor.subPath ?? []

  switch (anchor.position) {
    case 'before':
    case 'replace':
      return { path }
    case 'after': {
      const last = path[path.length - 1] ?? 0
      return { path: [...path.slice(0, -1), last + 1] }
    }
    case 'inside-start':
      return { path: [...path, 0, ...sub] }
    case 'inside-end':
      return { path: [...path, childCount, ...sub] }
    default: {
      const exhaustive: never = anchor.position
      return { error: `unknown anchor position: ${JSON.stringify(exhaustive)}` }
    }
  }
}
